import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MessageSquare, Send, Loader2, ArrowLeft, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactPage() {
    const { t } = useI18n();
    const navigate = useNavigate();
    const [subject, setSubject] = useState('');
    const [category, setCategory] = useState('general');
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [nickname, setNickname] = useState(''); // Honeypot field

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!subject.trim() || !message.trim()) {
            toast.error(t('errors.generic'));
            return;
        }

        setSending(true);
        try {
            const response = await fetch(`${API}/support/contact`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    subject,
                    category,
                    message,
                    nickname,
                }),
            });

            if (response.ok) {
                toast.success(t('contact.success'));
                // Wait a bit and redirect to dashboard
                setTimeout(() => navigate('/dashboard'), 2000);
            } else {
                const data = await response.json();
                toast.error(data.detail || t('errors.generic'));
            }
        } catch (error) {
            console.error('Contact submit error:', error);
            toast.error(t('errors.generic'));
        } finally {
            setSending(false);
        }
    };

    return (
        <Layout>
            <div className="p-6 md:p-8 max-w-2xl mx-auto">
                {/* Back Button */}
                <Button
                    variant="ghost"
                    onClick={() => navigate(-1)}
                    className="mb-6 text-stone hover:text-charcoal p-0 h-auto"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {t('back')}
                </Button>

                {/* Header */}
                <div className="mb-8">
                    <h1 className="font-manrope font-bold text-3xl text-charcoal mb-3">
                        {t('contact.title')}
                    </h1>
                    <p className="font-inter text-stone text-lg">
                        {t('contact.subtitle')}
                    </p>
                </div>

                <Card className="bg-white rounded-3xl shadow-soft p-6 md:p-8">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Category */}
                        <div className="space-y-2">
                            <Label className="font-manrope font-semibold text-charcoal">
                                {t('contact.form.category')}
                            </Label>
                            <Select value={category} onValueChange={setCategory}>
                                <SelectTrigger className="h-12 rounded-xl border-border bg-white/50 font-inter">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="general">{t('contact.form.categories.general')}</SelectItem>
                                    <SelectItem value="bug">{t('contact.form.categories.bug')}</SelectItem>
                                    <SelectItem value="suggestion">{t('contact.form.categories.suggestion')}</SelectItem>
                                    <SelectItem value="billing">{t('contact.form.categories.billing')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Subject */}
                        <div className="space-y-2">
                            <Label htmlFor="subject" className="font-manrope font-semibold text-charcoal">
                                {t('contact.form.subject')}
                            </Label>
                            <Input
                                id="subject"
                                placeholder={t('contact.form.subjectPlaceholder')}
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                required
                                className="h-12 rounded-xl border-border bg-white/50 font-inter"
                            />
                        </div>

                        {/* Message */}
                        <div className="space-y-2">
                            <Label htmlFor="message" className="font-manrope font-semibold text-charcoal">
                                {t('contact.form.message')}
                            </Label>
                            <Textarea
                                id="message"
                                placeholder={t('contact.form.messagePlaceholder')}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                required
                                maxLength={1000}
                                className="min-h-[200px] rounded-xl border-border bg-white/50 font-inter resize-none"
                            />
                            <div className="flex justify-end mt-1">
                                <span className={`text-xs font-inter ${message.length >= 900 ? 'text-destructive font-bold' : 'text-stone/60'}`}>
                                    {message.length} / 1000
                                </span>
                            </div>
                        </div>

                        {/* Honeypot field - hidden from users */}
                        <div className="sr-only" aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: '-9999px', opacity: 0, height: 0, width: 0, overflow: 'hidden' }}>
                            <label htmlFor="nickname">Nickname</label>
                            <input
                                id="nickname"
                                type="text"
                                name="nickname"
                                tabIndex="-1"
                                autoComplete="off"
                                value={nickname}
                                onChange={(e) => setNickname(e.target.value)}
                            />
                        </div>


                        {/* Submit Button */}
                        <Button
                            type="submit"
                            disabled={sending || !subject.trim() || !message.trim()}
                            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full py-6 font-manrope font-bold shadow-lg transition-all active:scale-95"
                        >
                            {sending ? (
                                <>
                                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                    {t('contact.form.sending')}
                                </>
                            ) : (
                                <>
                                    <Send className="w-5 h-5 mr-2" strokeWidth={1.5} />
                                    {t('contact.form.send')}
                                </>
                            )}
                        </Button>
                    </form>
                </Card>

                {/* Support Direct Link */}
                <div className="mt-12 text-center">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-primary/10 rounded-full mb-4">
                        <MessageSquare className="w-6 h-6 text-primary" strokeWidth={1.5} />
                    </div>
                    <p className="font-inter text-stone">
                        {t('contact.supportText')} <br />
                        <a href="mailto:info@mealplanner.es" className="text-charcoal font-semibold hover:underline">
                            info@mealplanner.es
                        </a>
                    </p>
                </div>
            </div>
        </Layout>
    );
}
